import React, { useEffect } from 'react'
import RefreshIcon from 'components/atoms/Icons/Controls/Refresh'
import { useSelector } from 'react-redux'
import { leaseTypes } from 'modules/api/portfolio/constants'
import { getCurrentLeaseType } from 'modules/ui/ChatBot'
import { List, AggregateListItem, Label } from 'components'
import styles from './PortfolioActivationForm.module.scss'

type TItem = {
  type?: 'email' | 'invoice'
  checked: boolean
  mainText: string
  secondaryText: string
  tertiaryText?: string
  recurring?: boolean
}

interface IPortfolioActivationFormProps {
  items: TItem[]
  fetchInvoiceSchedules: () => void
}

const renderItemText = (item: TItem): React.ReactNode =>
  item.recurring ? (
    <>
      {item.mainText}
      <Label circular className={styles['recurring-badge']}>
        <RefreshIcon viewBox="0 0 32 32" width={14} height={14} aria-hidden="true" />
        Recurring
      </Label>
    </>
  ) : (
    item.mainText
  )

const renderItems = (items: TItem[]): React.ReactElement => (
  <List className={styles['list-items']}>
    {items
      .filter((i: TItem) => !!i.checked)
      .map((item: TItem, i: number) => (
        <AggregateListItem
          key={i}
          active={!!item.checked}
          text={renderItemText(item)}
          secondaryText={item.secondaryText}
          tertiaryText={item.tertiaryText}
        />
      ))}
  </List>
)

const PortfolioActivationForm = ({
  items,
  fetchInvoiceSchedules,
}: IPortfolioActivationFormProps): React.ReactElement => {
  const leaseType = useSelector(getCurrentLeaseType)
  const isManagedLease = leaseType === leaseTypes.managed

  useEffect(() => {
    fetchInvoiceSchedules()
  }, [fetchInvoiceSchedules])

  // Both lease types split the immediate items into EMAILS and INVOICES sub-sections. Only
  // managed leases have recurring invoice templates, which add the "Monthly" note below.
  const emailItems = items.filter((i: TItem) => i.type === 'email')
  const invoiceItems = items.filter((i: TItem) => i.type === 'invoice')
  const hasRecurringInvoices = invoiceItems.some((i: TItem) => !!i.recurring)

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        <h3>On activation</h3>
        {isManagedLease && <p>The following will happen immediately.</p>}
      </div>

      {emailItems.length > 0 && (
        <>
          <div className={styles['sub-title']}>
            <h3>Emails</h3>
          </div>
          {renderItems(emailItems)}
        </>
      )}

      {invoiceItems.length > 0 && (
        <div className={styles['inline-list']}>
          <div className={styles['sub-title']}>
            <h3>The following draft invoices will be generated</h3>
          </div>
          {renderItems(invoiceItems)}
        </div>
      )}

      {hasRecurringInvoices && (
        <div className={styles.title}>
          <h3>Monthly</h3>
          <p>Recurring templates generate new draft invoices on the 10th of each month.</p>
        </div>
      )}
    </div>
  )
}

export default PortfolioActivationForm
